import { QueryClient } from '@tanstack/react-query';

// Auth-aware retry function that doesn't retry on authentication errors
const authAwareRetry = (failureCount: number, error: any) => {
  // Don't retry on authentication errors (401, 403)
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return false;
  }

  // Don't retry on client errors (4xx) except for 408, 429
  if (error?.response?.status >= 400 && error?.response?.status < 500) {
    if (error?.response?.status === 408 || error?.response?.status === 429) {
      return failureCount < 2; // Retry timeout and rate limit errors
    }
    return false;
  }

  // Retry server errors (5xx) and network errors up to 3 times
  return failureCount < 3;
};

// Create a QueryClient instance with custom configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Use auth-aware retry logic
      retry: authAwareRetry,
      // Retry with exponential backoff
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus (disabled to prevent unnecessary requests with stale tokens)
      refetchOnWindowFocus: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
      // Network mode - fail fast on auth errors
      networkMode: 'online',
    },
    mutations: {
      // Use auth-aware retry for mutations too
      retry: authAwareRetry,
      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Table data queries
  tableData: (apiUrl: string, params: Record<string, any>) =>
    ['tableData', apiUrl, params] as const,

  // Generic API queries
  api: (endpoint: string, params?: Record<string, any>) =>
    ['api', endpoint, params] as const,

  // User-specific queries
  user: (userId: string) => ['user', userId] as const,

  // Deal room queries
  dealRoom: (dealRoomId: string) => ['dealRoom', dealRoomId] as const,

  // Response queries
  response: (responseId: string) => ['response', responseId] as const,
} as const;

// Type for query keys
export type QueryKeys = typeof queryKeys;

// Cache management utilities for authentication scenarios
export const cacheManager = {
  // Clear all cached data on logout
  clearAllCache: () => {
    queryClient.clear();
  },

  // Clear specific user-related data
  clearUserData: (userId?: string) => {
    if (userId) {
      queryClient.removeQueries({ queryKey: ['user', userId] });
    } else {
      queryClient.removeQueries({ queryKey: ['user'] });
    }
  },

  // Clear all API data but keep static data
  clearApiData: () => {
    queryClient.removeQueries({ queryKey: ['api'] });
    queryClient.removeQueries({ queryKey: ['tableData'] });
  },

  // Invalidate all queries to force refetch with new token
  invalidateAll: () => {
    queryClient.invalidateQueries();
  },

  // Check if cache should be cleared based on Auth0 authentication state
  handleAuthStateChange: (isAuthenticated: boolean, user?: any) => {
    if (!isAuthenticated) {
      // User is logged out - clear everything
      queryClient.clear();
      // Clear localStorage items as well
      const keys = [
        'eptChannels',
        'forumIds',
        'selectedForumId',
        'selectForumName',
        'accessToken',
        'auth0User',
      ];
      keys.forEach(key => {
        localStorage.removeItem(key);
      });
    } else if (user) {
      // User is logged in - check if it's a different user
      const storedUserId = localStorage.getItem('auth0User');
      if (storedUserId && storedUserId !== user.sub) {
        // Different user logged in - clear cache for security
        queryClient.clear();
        console.log('Different user detected, clearing cache');
      }
    }
  },

  // Handle token expiry (legacy method for backward compatibility)
  handleTokenExpiry: () => {
    queryClient.clear();
    const keys = [
      'eptChannels',
      'forumIds',
      'selectedForumId',
      'selectForumName',
      'accessToken',
      'auth0User',
    ];
    keys.forEach(key => {
      localStorage.removeItem(key);
    });
  },

  // Auth-aware query function that checks authentication before making requests
  createAuthAwareQueryFn: <T>(
    queryFn: () => Promise<T>,
    isAuthenticated: boolean
  ) => {
    return async (): Promise<T> => {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }
      return queryFn();
    };
  },
};
